import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Package,
  MapPin,
  Calendar,
  Search,
  RefreshCw,
  CheckSquare,
  Square,
  BarChart3,
  Building2,
  Zap,
  Filter,
  Map,
  List,
  Navigation,
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useLazyGetSiteMetadataQuery,
  type PortfolioSite,
  type SiteMetadata,
} from '../../redux/api/portfolioApi';
import {
  useLazyListEntitiesQuery,
} from '../../redux/api/entityApi';
import {
  aggregatePortfolioComponents,
  getCategoryInfo,
  entityHasModules,
  getModuleCountFromEntity,
  parseSiteLocation,
  type PortfolioComponentData,
  type PortfolioComponentSummary,
  type ComponentModelSummary,
  type SiteComponentCount,
  type SelectedSiteData,
} from '../../services/portfolioComponentService';
import { setCurrentSite } from '../../redux/slices/siteSlice';
import {
  toggleSiteSelection,
  setSelectedSiteIds,
  clearSelectedSites,
  setIsLoading,
  setLoadingProgress,
  setSitesData,
  setPortfolioData,
  type SiteDataWithMetadata,
} from '../../redux/slices/portfolioSiteSelectionSlice';
import ComponentFilterPanel from '../portfolio/ComponentFilterPanel';
import { type ComponentFilter, type ModelWithColor } from '../portfolio/portfolioMapUtils';
import 'leaflet/dist/leaflet.css';

// Lazy-load map components to avoid loading leaflet for list view
const MapContainer = React.lazy(() => import('react-leaflet').then(m => ({ default: m.MapContainer })));
const TileLayer = React.lazy(() => import('react-leaflet').then(m => ({ default: m.TileLayer })));
const CircleMarker = React.lazy(() => import('react-leaflet').then(m => ({ default: m.CircleMarker })));
const Popup = React.lazy(() => import('react-leaflet').then(m => ({ default: m.Popup })));

type ViewMode = 'list' | 'map';

// ============================================================================
// Map Utilities (inline to avoid circular deps with PortfolioMapTab)
// ============================================================================

function parseLatLon(latLon: unknown): { lat: number; lng: number } | null {
  if (!latLon) return null;
  if (Array.isArray(latLon) && latLon.length >= 2) {
    const lat = typeof latLon[0] === 'number' ? latLon[0] : parseFloat(latLon[0]);
    const lng = typeof latLon[1] === 'number' ? latLon[1] : parseFloat(latLon[1]);
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
  }
  if (typeof latLon === 'string') {
    const parts = latLon.split(',').map(p => parseFloat(p.trim()));
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return { lat: parts[0], lng: parts[1] };
  }
  return null;
}

function getSiteCoordinates(
  site: PortfolioSite,
  metadata?: SiteMetadata | null,
  geocodedCoordinates?: { lat: number; lng: number } | null
): { lat: number; lng: number } | null {
  if (metadata?.site_lat_lon) {
    const coords = parseLatLon(metadata.site_lat_lon);
    if (coords) return coords;
  }
  if (geocodedCoordinates) return geocodedCoordinates;
  return parseSiteLocation(site);
}

interface SiteWithLocation extends PortfolioSite {
  coordinates: { lat: number; lng: number };
  siteAddress?: string | null;
}

// ============================================================================
// Component Map View (embedded in PortfolioComponentsTab)
// ============================================================================

interface ComponentMapViewProps {
  sitesData: SiteDataWithMetadata[];
  portfolioData: PortfolioComponentData | null;
  onNavigateToSite: (siteId: string) => void;
}

const ComponentMapView: React.FC<ComponentMapViewProps> = ({ sitesData, portfolioData, onNavigateToSite }) => {
  const [filter, setFilter] = useState<ComponentFilter>({ category: null, manufacturer: null, model: null });
  const [filteredModels, setFilteredModels] = useState<ModelWithColor[]>([]);
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null);
  const [selectedMapSiteId, setSelectedMapSiteId] = useState<string | null>(null);

  const sitesWithLocation = useMemo((): SiteWithLocation[] => {
    return sitesData
      .filter(sd => getSiteCoordinates(sd.site, sd.metadata, sd.geocodedCoordinates) !== null)
      .map(sd => ({
        ...sd.site,
        coordinates: getSiteCoordinates(sd.site, sd.metadata, sd.geocodedCoordinates)!,
        siteAddress: sd.metadata?.site_address || sd.site.address || sd.site.location || null,
      }));
  }, [sitesData]);

  const filteredSites = useMemo((): SiteWithLocation[] => {
    if (!filter.category && !filter.manufacturer && !filter.model) return sitesWithLocation;
    return sitesWithLocation.filter(site => {
      const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === (site.site_id || site.id));
      if (!siteData) return false;
      return siteData.entities.some(entity => {
        const componentType = entity.concept_id || entity.entity_type_id || (entity as any).pvcollada_component_type || '';
        const { category } = getCategoryInfo(componentType);
        if (filter.category === 'module') return entityHasModules(entity);
        if (filter.category && category !== filter.category) return false;
        if (filter.manufacturer) {
          const manufacturer = entity.manufacturer || (entity.properties as any)?.manufacturer || '';
          if (!manufacturer.toLowerCase().includes(filter.manufacturer.toLowerCase())) return false;
        }
        return true;
      });
    });
  }, [sitesWithLocation, sitesData, filter]);

  const getComponentCount = useCallback((siteId: string): number => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (!siteData) return 0;
    if (!filter.category && !filter.manufacturer && !filter.model) return siteData.entities.length;
    if (filter.category === 'module') {
      let count = 0;
      for (const entity of siteData.entities) count += getModuleCountFromEntity(entity);
      return count;
    }
    return siteData.entities.filter(entity => {
      const componentType = entity.concept_id || entity.entity_type_id || (entity as any).pvcollada_component_type || '';
      const { category } = getCategoryInfo(componentType);
      if (filter.category && category !== filter.category) return false;
      if (filter.manufacturer) {
        const manufacturer = entity.manufacturer || (entity.properties as any)?.manufacturer || '';
        if (!manufacturer.toLowerCase().includes(filter.manufacturer.toLowerCase())) return false;
      }
      return true;
    }).length;
  }, [sitesData, filter]);

  const getMarkerColor = useCallback((): string => {
    if (filter.category) {
      const summary = portfolioData?.summaries.find(s => s.component_category === filter.category);
      return summary?.color || '#10B981';
    }
    return '#10B981';
  }, [filter.category, portfolioData]);

  const getMarkerOpacity = useCallback((siteId: string, isHovered: boolean, isSelected: boolean): number => {
    if (isHovered || isSelected) return 0.95;
    if (filter.model) {
      const selectedModel = filteredModels.find(m => m.model === filter.model);
      if (selectedModel && !selectedModel.siteIds.has(siteId)) return 0.25;
    }
    return 0.7;
  }, [filter.model, filteredModels]);

  const getSiteComponentInfo = useCallback((siteId: string) => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (!siteData) return { inverterModels: [] as string[], moduleModels: [] as string[] };
    const inverterModels = new Set<string>();
    const moduleModels = new Set<string>();
    for (const entity of siteData.entities) {
      const componentType = entity.concept_id || entity.entity_type_id || (entity as any).pvcollada_component_type || '';
      const { category } = getCategoryInfo(componentType);
      const model = entity.model || (entity.properties as any)?.model || entity.name || '';
      if (category === 'inverter' && model) inverterModels.add(model);
      else if (category === 'module' && model) moduleModels.add(model);
    }
    return { inverterModels: Array.from(inverterModels), moduleModels: Array.from(moduleModels) };
  }, [sitesData]);

  if (sitesWithLocation.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <MapPin className="w-16 h-16 eg-text-muted mb-4" />
        <h2 className="text-lg font-medium eg-text-primary mb-2">No sites with coordinates</h2>
        <p className="text-sm eg-text-secondary">Sites need location data (lat/lon or address) to appear on the map.</p>
      </div>
    );
  }

  const defaultCenter: [number, number] = [39.8283, -98.5795];
  const color = getMarkerColor();

  return (
    <div className="w-full h-full relative">
      <React.Suspense fallback={
        <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
          <Loader2 className="w-8 h-8 text-engramiq-green animate-spin" />
        </div>
      }>
        <MapContainer center={defaultCenter} zoom={4} className="w-full h-full" style={{ background: '#0f172a' }}>
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          />
          {filteredSites.map(site => {
            const siteId = site.site_id || site.id || '';
            const count = getComponentCount(siteId);
            const isHovered = hoveredSiteId === siteId;
            const isSelected = selectedMapSiteId === siteId;
            const baseSize = 8;
            const maxSize = 24;
            const size = Math.min(baseSize + Math.log10(count + 1) * 6, maxSize);
            const opacity = getMarkerOpacity(siteId, isHovered, isSelected);
            const componentInfo = getSiteComponentInfo(siteId);

            return (
              <CircleMarker
                key={siteId}
                center={[site.coordinates.lat, site.coordinates.lng]}
                radius={isHovered || isSelected ? size * 1.3 : size}
                pathOptions={{
                  fillColor: color,
                  fillOpacity: opacity,
                  color: isSelected ? '#fff' : color,
                  weight: isSelected ? 3 : 1,
                }}
                eventHandlers={{
                  click: () => setSelectedMapSiteId(siteId),
                  mouseover: () => setHoveredSiteId(siteId),
                  mouseout: () => setHoveredSiteId(null),
                }}
              >
                <Popup>
                  <div className="min-w-56 max-w-72">
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {site.site_name || site.name || 'Unknown Site'}
                    </h3>
                    {site.siteAddress && (
                      <p className="text-xs text-gray-500 mb-2">{site.siteAddress}</p>
                    )}
                    <p className="text-sm text-gray-600 mb-2">
                      {count.toLocaleString()} {filter.category ? getCategoryInfo(filter.category).displayName.toLowerCase() : 'components'}
                    </p>
                    {componentInfo.inverterModels.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                          <Zap className="w-3 h-3 text-amber-500" /> Inverters:
                        </p>
                        <div className="ml-4 text-xs text-gray-600">
                          {componentInfo.inverterModels.slice(0, 3).map((model, i) => (
                            <div key={i} className="truncate">{model}</div>
                          ))}
                          {componentInfo.inverterModels.length > 3 && (
                            <div className="text-gray-400">+{componentInfo.inverterModels.length - 3} more</div>
                          )}
                        </div>
                      </div>
                    )}
                    {componentInfo.moduleModels.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                          <Package className="w-3 h-3 text-blue-500" /> Modules:
                        </p>
                        <div className="ml-4 text-xs text-gray-600">
                          {componentInfo.moduleModels.slice(0, 3).map((model, i) => (
                            <div key={i} className="truncate">{model}</div>
                          ))}
                          {componentInfo.moduleModels.length > 3 && (
                            <div className="text-gray-400">+{componentInfo.moduleModels.length - 3} more</div>
                          )}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => onNavigateToSite(siteId)}
                      className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                    >
                      <Navigation className="w-3.5 h-3.5" />
                      View Site
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </React.Suspense>

      {/* Component Filter Panel */}
      {portfolioData && (
        <ComponentFilterPanel
          portfolioData={portfolioData}
          filter={filter}
          onFilterChange={setFilter}
          onModelsChange={setFilteredModels}
        />
      )}

      {/* Status Bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] backdrop-blur rounded-lg px-4 py-2" style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-card)' }}>
        <div className="flex items-center gap-4 text-xs">
          <span className="eg-text-secondary">
            <span className="text-engramiq-green font-medium">{filteredSites.length}</span> sites shown
          </span>
          {portfolioData && (
            <>
              <span className="eg-text-muted">|</span>
              <span className="eg-text-secondary">
                <span className="text-engramiq-green font-medium">{portfolioData.total_components.toLocaleString()}</span> total components
              </span>
            </>
          )}
          {filter.category && (
            <>
              <span className="eg-text-muted">|</span>
              <span className="eg-text-secondary">
                Showing: {getCategoryInfo(filter.category).displayName}
                {filter.model && ` > ${filter.model}`}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Site Selection Modal Component
// ============================================================================

interface SiteSelectionModalProps {
  sites: PortfolioSite[];
  selectedSiteIds: Set<string>;
  onToggleSite: (siteId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onClose: () => void;
  onConfirm: () => void;
}

const SiteSelectionModal: React.FC<SiteSelectionModalProps> = ({
  sites,
  selectedSiteIds,
  onToggleSite,
  onSelectAll,
  onClearAll,
  onClose,
  onConfirm,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSites = useMemo(() => {
    if (!searchTerm) return sites;
    const term = searchTerm.toLowerCase();
    return sites.filter(site =>
      (site.site_name || site.name || '').toLowerCase().includes(term)
    );
  }, [sites, searchTerm]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'var(--color-modal-backdrop)' }}>
      <div className="eg-card-elevated w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4" style={{ borderBottom: '1px solid var(--color-overlay-light)' }}>
          <h2 className="text-lg font-semibold eg-text-primary">Select Sites</h2>
          <p className="text-sm eg-text-secondary mt-1">
            Choose sites to include in the portfolio component inventory
          </p>
        </div>

        <div className="p-4" style={{ borderBottom: '1px solid var(--color-overlay-light)' }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 eg-text-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search sites..."
              className="eg-input-search"
            />
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={onSelectAll}
              className="px-3 py-1.5 text-xs bg-engramiq-green/20 text-engramiq-green rounded-lg hover:bg-engramiq-green/30 transition-colors"
            >
              Select All
            </button>
            <button
              onClick={onClearAll}
              className="px-3 py-1.5 text-xs eg-text-secondary rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
              style={{ background: 'var(--color-surface)' }}
            >
              Clear All
            </button>
            <span className="ml-auto text-xs eg-text-secondary self-center">
              {selectedSiteIds.size} of {sites.length} selected
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-2">
          {filteredSites.map(site => {
            const siteId = site.site_id || site.id || '';
            const isSelected = selectedSiteIds.has(siteId);
            return (
              <button
                key={siteId}
                onClick={() => onToggleSite(siteId)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isSelected
                    ? 'bg-engramiq-green/20'
                    : 'hover:bg-[var(--color-surface-hover)]'
                }`}
                style={isSelected ? { border: '1px solid rgba(23, 196, 128, 0.3)' } : { border: '1px solid transparent' }}
              >
                {isSelected ? (
                  <CheckSquare className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                ) : (
                  <Square className="w-4 h-4 flex-shrink-0 eg-text-muted" />
                )}
                <div className="flex-1 text-left">
                  <span className={`text-sm ${isSelected ? 'eg-text-primary' : 'eg-text-secondary'}`}>
                    {site.site_name || site.name || 'Unknown Site'}
                  </span>
                  {site.commissioning_date && (
                    <span className="text-xs eg-text-muted ml-2">
                      COD: {new Date(site.commissioning_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-4 flex gap-3 justify-end" style={{ borderTop: '1px solid var(--color-overlay-light)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm eg-text-secondary rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
            style={{ background: 'var(--color-surface)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={selectedSiteIds.size === 0}
            className="px-4 py-2 text-sm bg-engramiq-green text-engramiq-dark-blue font-medium rounded-lg hover:bg-engramiq-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Load Components ({selectedSiteIds.size} sites)
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Age Distribution Chart Component
// ============================================================================

interface AgeDistributionChartProps {
  distribution: PortfolioComponentSummary['age_distribution'];
}

const AgeDistributionChart: React.FC<AgeDistributionChartProps> = ({ distribution }) => {
  if (!distribution || distribution.buckets.length === 0) return null;

  const maxCount = Math.max(...distribution.buckets.map(b => b.count));

  return (
    <div className="mt-4 p-3 rounded-lg" style={{ background: 'var(--color-bg-muted)' }}>
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-engramiq-green" />
        <span className="text-xs font-medium eg-text-primary">Age Distribution</span>
        <span className="text-xs eg-text-secondary ml-auto">
          Avg: {Math.round(distribution.average_age_days / 365 * 10) / 10} years
        </span>
      </div>
      <div className="flex items-end gap-1 h-16">
        {distribution.buckets.map((bucket, idx) => (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div
              className="w-full bg-engramiq-green/60 rounded-t transition-all"
              style={{ height: `${(bucket.count / maxCount) * 100}%`, minHeight: bucket.count > 0 ? '4px' : '0' }}
            />
            <span className="text-[10px] eg-text-muted whitespace-nowrap">{bucket.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Model Row Component (Expandable)
// ============================================================================

interface ModelRowProps {
  model: ComponentModelSummary;
  onSiteClick: (siteId: string) => void;
}

const ModelRow: React.FC<ModelRowProps> = ({ model, onSiteClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div style={{ borderBottom: '1px solid var(--color-overlay-light)' }} className="last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-surface-hover)] transition-colors text-left"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-engramiq-green flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 flex-shrink-0 eg-text-muted" />
        )}
        <span className="text-sm eg-text-secondary w-32 flex-shrink-0 truncate">
          {model.manufacturer}
        </span>
        <span className="text-sm eg-text-primary flex-1 truncate">
          {model.model}
        </span>
        <span className="text-sm font-medium text-engramiq-green w-16 text-right">
          {model.total_count.toLocaleString()}
        </span>
        <span className="text-sm eg-text-secondary w-16 text-right">
          {model.site_distribution.length}
        </span>
        <span className="text-sm eg-text-secondary w-16 text-right">
          {model.average_age_years !== null ? `${model.average_age_years}yr` : '-'}
        </span>
      </button>

      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--color-overlay-light)', background: 'var(--color-bg-muted)' }}>
          <div className="px-4 py-2 flex items-center gap-3" style={{ borderBottom: '1px solid var(--color-overlay-light)', background: 'var(--color-surface)' }}>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-8"></span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider flex-1">Site Name</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-16 text-right">Count</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-24 text-right">COD</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-20 text-right">Age</span>
          </div>
          {model.site_distribution.slice(0, 10).map(site => (
            <button
              key={site.site_id}
              onClick={() => onSiteClick(site.site_id)}
              className="w-full flex items-center gap-3 px-4 py-2 hover:bg-engramiq-green/10 transition-colors text-left"
            >
              <MapPin className="w-3 h-3 eg-text-muted flex-shrink-0" />
              <span className="text-sm eg-text-primary flex-1 truncate">
                {site.site_name}
              </span>
              <span className="text-sm text-engramiq-green w-16 text-right">
                {site.count}
              </span>
              <span className="text-sm eg-text-secondary w-24 text-right">
                {site.cod_date ? new Date(site.cod_date).toLocaleDateString() : '-'}
              </span>
              <span className="text-sm eg-text-secondary w-20 text-right">
                {site.component_age_days !== null
                  ? `${Math.round(site.component_age_days / 365 * 10) / 10} years`
                  : '-'}
              </span>
            </button>
          ))}
          {model.site_distribution.length > 10 && (
            <div className="px-4 py-2 text-center">
              <span className="text-xs eg-text-muted">
                + {model.site_distribution.length - 10} more sites
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Category Card Component
// ============================================================================

interface CategoryCardProps {
  summary: PortfolioComponentSummary;
  onSiteClick: (siteId: string) => void;
  isSelected: boolean;
  onSelect: () => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ summary, onSiteClick, isSelected, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`eg-card-elevated overflow-hidden transition-all ${
        isSelected
          ? 'ring-1 ring-engramiq-green'
          : ''
      }`}
    >
      <div
        className="flex items-center gap-3 p-4 cursor-pointer"
        onClick={onSelect}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${summary.color}20` }}
        >
          <Zap className="w-5 h-5" style={{ color: summary.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold eg-text-primary truncate">
            {summary.display_name}
          </h3>
          <p className="text-xs eg-text-secondary">
            {summary.unique_model_count} unique model{summary.unique_model_count !== 1 ? 's' : ''} over {summary.site_count} site{summary.site_count !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="text-right">
          <span className="text-lg font-bold text-engramiq-green">
            {summary.total_count.toLocaleString()}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="p-1 hover:bg-engramiq-green/10 rounded transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-engramiq-green" />
          ) : (
            <ChevronRight className="w-5 h-5 eg-text-muted" />
          )}
        </button>
      </div>

      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--color-overlay-light)' }}>
          <div className="px-4 py-2 flex items-center gap-3" style={{ background: 'var(--color-surface)', borderBottom: '1px solid var(--color-overlay-light)' }}>
            <span className="w-4"></span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-32">Manufacturer</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider flex-1">Model</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-16 text-right">Count</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-16 text-right">Sites</span>
            <span className="text-xs font-medium eg-text-muted uppercase tracking-wider w-16 text-right">Age</span>
          </div>
          <div className="max-h-96 overflow-auto">
            {summary.models.map(model => (
              <ModelRow
                key={model.normalized_id}
                model={model}
                onSiteClick={onSiteClick}
              />
            ))}
          </div>
          <AgeDistributionChart distribution={summary.age_distribution} />
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const PortfolioComponentsTab: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const selectedPortfolioId = useAppSelector(state => state.portfolio.selectedPortfolioId);
  const organizationState = useAppSelector(s => s.organization);
  const profile = useAppSelector(state => state.auth.profile);
  const currentSite = useAppSelector(state => state.site.currentSite);

  // Shared portfolio site selection state from Redux
  const {
    selectedSiteIds: selectedSiteIdsArray,
    sitesData,
    portfolioData,
    isLoading,
    loadingProgress,
    hasLoadedData,
  } = useAppSelector(state => state.portfolioSiteSelection);

  // Convert array to Set for easier lookups
  const selectedSiteIds = useMemo(() => new Set(selectedSiteIdsArray), [selectedSiteIdsArray]);

  const userEmail = (profile.email || '').toLowerCase();
  const isSuperAdmin = userEmail.endsWith('@engramiq.com');
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  // Component state (local UI state only)
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Queries
  const adminArgs = isSuperAdmin
    ? (currentOrgId ? { organization_id: currentOrgId } : undefined)
    : undefined;

  const usePortfolioScoping = !!selectedPortfolioId;

  const { data: adminSitesResponse, isLoading: adminSitesLoading } = useListAdminSitesQuery(
    adminArgs,
    { skip: !isSuperAdmin || usePortfolioScoping }
  );

  const { data: portfolioSitesResponse, isLoading: portfolioSitesLoading } = useListPortfolioSitesQuery(
    selectedPortfolioId ? { portfolio_id: selectedPortfolioId } : { portfolio_id: '' },
    { skip: !selectedPortfolioId }
  );

  // Entity and metadata fetching
  const [fetchEntities] = useLazyListEntitiesQuery();
  const [fetchSiteMetadata] = useLazyGetSiteMetadataQuery();

  // Get available sites
  const availableSites = useMemo(() => {
    if (usePortfolioScoping) {
      return ((portfolioSitesResponse as any)?.items || []) as PortfolioSite[];
    }
    if (isSuperAdmin) {
      return ((adminSitesResponse as any)?.items || []) as PortfolioSite[];
    }
    return ((portfolioSitesResponse as any)?.items || []) as PortfolioSite[];
  }, [usePortfolioScoping, isSuperAdmin, adminSitesResponse, portfolioSitesResponse]);

  const sitesLoading = isSuperAdmin ? adminSitesLoading : portfolioSitesLoading;

  // Site selection handlers - dispatch to Redux
  const handleToggleSite = useCallback((siteId: string) => {
    dispatch(toggleSiteSelection(siteId));
  }, [dispatch]);

  const handleSelectAll = useCallback(() => {
    dispatch(setSelectedSiteIds(availableSites.map(s => s.site_id || s.id || '')));
  }, [dispatch, availableSites]);

  const handleClearAll = useCallback(() => {
    dispatch(clearSelectedSites());
  }, [dispatch]);

  // Load component data for selected sites
  const loadComponentData = useCallback(async () => {
    if (selectedSiteIds.size === 0) return;

    dispatch(setIsLoading(true));
    dispatch(setLoadingProgress({ current: 0, total: selectedSiteIds.size }));
    setShowSiteSelector(false);

    const loadedSites: SiteDataWithMetadata[] = [];
    const siteIdsArray = Array.from(selectedSiteIds);

    for (let i = 0; i < siteIdsArray.length; i++) {
      const siteId = siteIdsArray[i];
      const site = availableSites.find(s => (s.site_id || s.id) === siteId)
        || (currentSite && (currentSite.site_id || currentSite.id) === siteId ? currentSite : null);

      if (!site) continue;

      const portfolioId = site.portfolio_id;

      try {
        // Fetch entities and metadata in parallel
        const [entities, metadataResult] = await Promise.all([
          fetchEntities({
            portfolio_id: portfolioId,
            site_id: siteId,
          }).unwrap(),
          fetchSiteMetadata({
            portfolio_id: portfolioId,
            site_id: siteId,
          }).unwrap().catch(() => null),
        ]);

        loadedSites.push({
          site,
          entities: entities || [],
          connections: [],
          metadata: metadataResult,
        });
      } catch (error) {
        console.warn(`Failed to fetch entities for site ${siteId}:`, error);
      }

      dispatch(setLoadingProgress({ current: i + 1, total: siteIdsArray.length }));
    }

    dispatch(setSitesData(loadedSites));
    dispatch(setIsLoading(false));
  }, [selectedSiteIds, availableSites, currentSite, fetchEntities, fetchSiteMetadata, dispatch]);

  // Auto-load all portfolio sites when available, or fall back to current site
  const autoLoadTriggered = React.useRef(false);
  useEffect(() => {
    if (autoLoadTriggered.current || hasLoadedData || isLoading) return;

    // Prefer all portfolio sites
    if (availableSites.length > 0) {
      autoLoadTriggered.current = true;
      const allIds = availableSites.map(s => s.site_id || s.id || '').filter(Boolean);
      dispatch(setSelectedSiteIds(allIds));
      return;
    }

    // Fall back to current site if no portfolio sites yet
    if (currentSite) {
      const siteId = currentSite.site_id || currentSite.id || '';
      if (siteId) {
        autoLoadTriggered.current = true;
        dispatch(setSelectedSiteIds([siteId]));
      }
    }
  }, [availableSites, currentSite, hasLoadedData, isLoading, dispatch]);

  // Trigger load once site IDs are set from auto-select
  useEffect(() => {
    if (autoLoadTriggered.current && selectedSiteIds.size > 0 && !isLoading && !hasLoadedData) {
      loadComponentData();
    }
  }, [selectedSiteIds.size, isLoading, hasLoadedData, loadComponentData]);

  // Aggregate data when sites are loaded
  useEffect(() => {
    if (sitesData.length > 0) {
      const data = aggregatePortfolioComponents(sitesData);
      dispatch(setPortfolioData(data));
    }
  }, [sitesData, dispatch]);

  // Filter summaries based on search
  const filteredSummaries = useMemo(() => {
    if (!portfolioData) return [];
    if (!searchTerm) return portfolioData.summaries;

    const term = searchTerm.toLowerCase();
    return portfolioData.summaries.filter(summary =>
      summary.display_name.toLowerCase().includes(term) ||
      summary.component_category.toLowerCase().includes(term) ||
      summary.models.some(m =>
        m.manufacturer.toLowerCase().includes(term) ||
        m.model.toLowerCase().includes(term)
      )
    );
  }, [portfolioData, searchTerm]);

  // Handle site click to navigate to site view
  const handleSiteClick = useCallback((siteId: string) => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (siteData) {
      dispatch(setCurrentSite(siteData.site));
      // Open component list tab for the site
      window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'componentlist' } }));
    }
  }, [sitesData, dispatch]);

  // Loading state for initial site list
  if (sitesLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="eg-text-primary">Loading sites...</p>
        </div>
      </div>
    );
  }

  // Loading state for component data
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="eg-text-primary mb-2">Loading component data...</p>
          <p className="text-sm eg-text-secondary">
            {loadingProgress.current} of {loadingProgress.total} sites
          </p>
          <div className="w-64 h-2 rounded-full mt-3 mx-auto overflow-hidden" style={{ background: 'var(--color-surface)' }}>
            <div
              className="h-full bg-engramiq-green transition-all duration-300"
              style={{ width: `${(loadingProgress.current / loadingProgress.total) * 100}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-toolbar-header p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-engramiq-green" />
            <div>
              <h1 className="text-xl font-bold eg-text-primary">Portfolio Component Inventory</h1>
              {portfolioData && (
                <p className="text-sm eg-text-secondary mt-0.5">
                  {portfolioData.total_components.toLocaleString()} components across {portfolioData.total_sites} sites
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* View toggle */}
            {portfolioData && (
              <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-border)' }}>
                <button
                  onClick={() => setViewMode('list')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-engramiq-green/20 text-engramiq-green'
                      : 'eg-text-secondary hover:bg-[var(--color-surface-hover)]'
                  }`}
                >
                  <List className="w-3.5 h-3.5" />
                  List
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                    viewMode === 'map'
                      ? 'bg-engramiq-green/20 text-engramiq-green'
                      : 'eg-text-secondary hover:bg-[var(--color-surface-hover)]'
                  }`}
                  style={{ borderLeft: '1px solid var(--color-border)' }}
                >
                  <Map className="w-3.5 h-3.5" />
                  Map
                </button>
              </div>
            )}
            <button
              onClick={() => setShowSiteSelector(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm eg-text-primary hover:bg-[var(--color-surface-hover)] transition-colors"
              style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
            >
              <Building2 className="w-4 h-4" />
              Select Sites ({selectedSiteIds.size})
            </button>
            {portfolioData && (
              <button
                onClick={loadComponentData}
                className="flex items-center gap-2 px-4 py-2 bg-engramiq-green/20 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-green hover:bg-engramiq-green/30 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        {portfolioData && (
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 eg-text-muted" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search components, manufacturers, models..."
              className="eg-input-search"
            />
          </div>
        )}
      </div>

      {/* Content */}
      {viewMode === 'map' && portfolioData ? (
        <div className="flex-1 min-h-0">
          <ComponentMapView
            sitesData={sitesData}
            portfolioData={portfolioData}
            onNavigateToSite={handleSiteClick}
          />
        </div>
      ) : (
        <div className="eg-page-content p-6">
          {!portfolioData ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Package className="w-16 h-16 eg-text-muted mb-4" />
              <h2 className="text-lg font-medium eg-text-primary mb-2">
                No sites selected
              </h2>
              <p className="text-sm eg-text-secondary mb-6 max-w-md">
                Select sites from your portfolio to view aggregated component inventory data across all selected locations.
              </p>
              <button
                onClick={() => setShowSiteSelector(true)}
                className="flex items-center gap-2 px-6 py-3 bg-engramiq-green text-engramiq-dark-blue font-medium rounded-lg hover:bg-engramiq-green/90 transition-colors"
              >
                <Building2 className="w-5 h-5" />
                Select Sites
              </button>
            </div>
          ) : filteredSummaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Filter className="w-16 h-16 eg-text-muted mb-4" />
              <h2 className="text-lg font-medium eg-text-primary mb-2">
                No components match your search
              </h2>
              <p className="text-sm eg-text-secondary">
                Try adjusting your search term or clear the filter
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredSummaries.map(summary => (
                <CategoryCard
                  key={summary.component_category}
                  summary={summary}
                  onSiteClick={handleSiteClick}
                  isSelected={selectedCategory === summary.component_category}
                  onSelect={() => setSelectedCategory(
                    selectedCategory === summary.component_category ? null : summary.component_category
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Site Selection Modal */}
      {showSiteSelector && (
        <SiteSelectionModal
          sites={availableSites}
          selectedSiteIds={selectedSiteIds}
          onToggleSite={handleToggleSite}
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
          onClose={() => setShowSiteSelector(false)}
          onConfirm={loadComponentData}
        />
      )}
    </div>
  );
};

export default PortfolioComponentsTab;
